/* ============================================================
   Forgemier — base.js
   Vanilla, no dependencies. Progressive enhancement.
   ============================================================ */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- utilities ---------- */
  function on(el, evt, handler, opts) {
    if (!el) return;
    el.addEventListener(evt, handler, opts || false);
  }

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function trapFocus(container, initial) {
    if (!container) return function () {};
    var focusable = qsa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', container);
    var first = focusable[0];
    var last  = focusable[focusable.length - 1];
    var previouslyFocused = document.activeElement;
    if (initial && initial.focus) initial.focus(); else if (first) first.focus();

    function handle(e) {
      if (e.key !== 'Tab') return;
      if (focusable.length === 0) { e.preventDefault(); return; }
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handle);
    return function release() {
      container.removeEventListener('keydown', handle);
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }

  /* ---------- Bag drawer ---------- */
  var Bag = (function () {
    var drawer, releaseFocus;

    function open(e) {
      if (e) e.preventDefault();
      drawer = qs('[data-bag-drawer]');
      if (!drawer) return;
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(function () { drawer.classList.add('is-open'); });
      document.body.style.overflow = 'hidden';
      releaseFocus = trapFocus(drawer, qs('[data-bag-close]', drawer));
      document.addEventListener('keydown', escClose);
    }

    function close() {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', escClose);
      if (releaseFocus) releaseFocus();
      window.setTimeout(function () { drawer.hidden = true; }, prefersReducedMotion ? 0 : 400);
    }

    function escClose(e) { if (e.key === 'Escape') close(); }

    function refresh() {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var count = qs('[data-bag-count]');
          if (count) count.textContent = cart.item_count;
          var subtotal = qs('[data-bag-subtotal]');
          if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
        })
        .catch(function () {});
    }

    function attach() {
      qsa('[data-bag-toggle]').forEach(function (btn) { on(btn, 'click', open); });
      qsa('[data-bag-close]').forEach(function (btn) { on(btn, 'click', close); });

      // Intercept product form submit -> fetch add
      qsa('form[action$="/cart/add"], form[data-product-form]').forEach(function (form) {
        on(form, 'submit', function (e) {
          e.preventDefault();
          var btn = form.querySelector('[type="submit"]');
          if (btn && btn.disabled) return;
          if (btn) btn.setAttribute('aria-busy', 'true');
          var formData = new FormData(form);
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData
          })
            .then(function (r) { return r.json(); })
            .then(function () {
              refresh();
              open();
            })
            .catch(function () { form.submit(); })
            .finally(function () { if (btn) btn.removeAttribute('aria-busy'); });
        });
      });
    }

    return { attach: attach, open: open, close: close, refresh: refresh };
  })();

  /* ---------- Predictive search ---------- */
  var Search = (function () {
    var panel, input, results, releaseFocus;
    var t;

    function open(e) {
      if (e) e.preventDefault();
      panel = qs('[data-predictive-search]');
      if (!panel) return;
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      input = qs('[data-predictive-input]', panel);
      results = qs('[data-predictive-results]', panel);
      if (input) input.focus();
      releaseFocus = trapFocus(panel, input);
      document.addEventListener('keydown', esc);
    }

    function close() {
      if (!panel) return;
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', esc);
      if (releaseFocus) releaseFocus();
    }

    function esc(e) { if (e.key === 'Escape') close(); }

    function query(term) {
      if (!term || term.length < 2) { if (results) results.innerHTML = ''; return; }
      var url = '/search/suggest.json?q=' + encodeURIComponent(term) + '&resources[type]=product,collection,article,page&resources[limit]=6';
      fetch(url, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) { render(data.resources && data.resources.results); })
        .catch(function () {});
    }

    function render(r) {
      if (!results) return;
      if (!r) { results.innerHTML = ''; return; }
      var html = '';
      if (r.products && r.products.length) {
        html += '<div class="predictive-search__group"><h3>Pieces</h3><ul class="predictive-search__list">';
        r.products.forEach(function (p) { html += '<li><a href="' + p.url + '">' + p.title + '</a></li>'; });
        html += '</ul></div>';
      }
      if (r.collections && r.collections.length) {
        html += '<div class="predictive-search__group"><h3>Collections</h3><ul class="predictive-search__list">';
        r.collections.forEach(function (c) { html += '<li><a href="' + c.url + '">' + c.title + '</a></li>'; });
        html += '</ul></div>';
      }
      if (r.articles && r.articles.length) {
        html += '<div class="predictive-search__group"><h3>Journal</h3><ul class="predictive-search__list">';
        r.articles.forEach(function (a) { html += '<li><a href="' + a.url + '">' + a.title + '</a></li>'; });
        html += '</ul></div>';
      }
      if (r.pages && r.pages.length) {
        html += '<div class="predictive-search__group"><h3>Pages</h3><ul class="predictive-search__list">';
        r.pages.forEach(function (p) { html += '<li><a href="' + p.url + '">' + p.title + '</a></li>'; });
        html += '</ul></div>';
      }
      if (!html) html = '<p class="predictive-search__empty">No matches yet.</p>';
      results.innerHTML = html;
    }

    function attach() {
      qsa('[data-search-toggle]').forEach(function (b) { on(b, 'click', open); });
      qsa('[data-search-close]').forEach(function (b) { on(b, 'click', close); });
      document.addEventListener('input', function (e) {
        if (!e.target.matches('[data-predictive-input]')) return;
        window.clearTimeout(t);
        var val = e.target.value;
        t = window.setTimeout(function () { query(val); }, 180);
      });
    }

    return { attach: attach, open: open, close: close };
  })();

  /* ---------- Mobile menu ---------- */
  var Mobile = (function () {
    var panel, releaseFocus;
    function open() {
      panel = qs('[data-mobile-menu]');
      if (!panel) return;
      panel.hidden = false;
      requestAnimationFrame(function () { panel.classList.add('is-open'); });
      document.body.style.overflow = 'hidden';
      releaseFocus = trapFocus(panel, qs('[data-mobile-menu-close]', panel));
      document.addEventListener('keydown', esc);
      var toggle = qs('[data-mobile-menu-toggle]'); if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }
    function close() {
      if (!panel) return;
      panel.classList.remove('is-open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', esc);
      if (releaseFocus) releaseFocus();
      var toggle = qs('[data-mobile-menu-toggle]'); if (toggle) toggle.setAttribute('aria-expanded', 'false');
      window.setTimeout(function () { panel.hidden = true; }, prefersReducedMotion ? 0 : 400);
    }
    function esc(e) { if (e.key === 'Escape') close(); }
    function attach() {
      qsa('[data-mobile-menu-toggle]').forEach(function (b) { on(b, 'click', open); });
      qsa('[data-mobile-menu-close]').forEach(function (b) { on(b, 'click', close); });
    }
    return { attach: attach };
  })();

  /* ---------- Product media gallery ---------- */
  var Gallery = (function () {
    function attach() {
      var media = qs('[data-product-media]');
      if (!media) return;
      var thumbs = qsa('[data-media-index]', media);
      var mainImg = qs('.main-product__media-main img', media);
      var all = qsa('.main-product__thumb img', media);
      thumbs.forEach(function (thumb, i) {
        on(thumb, 'click', function () {
          if (!mainImg || !all[i]) return;
          var src = all[i].getAttribute('src').replace(/width=\d+/, 'width=1200');
          mainImg.setAttribute('src', src);
          thumbs.forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
        });
      });
    }
    return { attach: attach };
  })();

  /* ---------- Variant picker ---------- */
  var Variants = (function () {
    function attach() {
      var form = qs('[data-product-form]');
      if (!form) return;
      var selects = qsa('[data-option-selector]', form);
      if (!selects.length) return;

      selects.forEach(function (sel) {
        on(sel, 'change', function () { update(form); });
      });
    }

    function currentOptions(form) {
      return qsa('[data-option-selector]', form).map(function (s) { return s.value; });
    }

    function update(form) {
      var productJsonUrl = form.getAttribute('data-product-url');
      // Use product handle from window path if available
      var handle = (window.location.pathname.match(/\/products\/([^\/]+)/) || [])[1];
      if (!handle) return;
      fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (product) {
          var opts = currentOptions(form);
          var match = product.variants.find(function (v) {
            for (var i = 0; i < opts.length; i++) { if (v.options[i] !== opts[i]) return false; }
            return true;
          });
          if (!match) return;
          var hidden = qs('[data-variant-id]', form);
          if (hidden) hidden.value = match.id;
          var priceEl = qs('.main-product__price [data-price]');
          if (priceEl) priceEl.querySelector('.price__current') && (priceEl.querySelector('.price__current').textContent = formatMoney(match.price));
          var btn = qs('[data-acquire-button]', form);
          var label = qs('[data-acquire-label]', form);
          if (btn && label) {
            if (!match.available) {
              btn.setAttribute('disabled', 'disabled');
              btn.setAttribute('aria-disabled', 'true');
              label.textContent = 'Unavailable';
            } else {
              btn.removeAttribute('disabled');
              btn.removeAttribute('aria-disabled');
              label.textContent = 'Acquire';
            }
          }
          // Media swap based on variant.featured_media
          if (match.featured_media && match.featured_media.id) {
            var slides = qsa('[data-media-slide]');
            var thumbs = qsa('.main-product__thumb');
            var mediaId = String(match.featured_media.id);
            slides.forEach(function (s) {
              s.classList.toggle('is-active', String(s.getAttribute('data-media-id')) === mediaId);
            });
            thumbs.forEach(function (t) {
              t.classList.toggle('is-active', String(t.getAttribute('data-media-id')) === mediaId);
            });
          }
          // Update URL
          if (history.replaceState) {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', match.id);
            history.replaceState({}, '', url.toString());
          }
        })
        .catch(function () {});
    }

    return { attach: attach };
  })();

  /* ---------- Money formatting (uses Shopify's total_price in cents) ---------- */
  function formatMoney(cents) {
    var n = (cents / 100).toFixed(2);
    return '$' + n;
  }

  /* ---------- Quantity stepper ---------- */
  var Quantity = (function () {
    function attach() {
      qsa('[data-quantity-stepper]').forEach(function (stepper) {
        var input = qs('[data-quantity-input]', stepper);
        if (!input) return;
        var dec = qs('[data-quantity-decrement]', stepper);
        var inc = qs('[data-quantity-increment]', stepper);
        var min = parseInt(input.getAttribute('min') || '0', 10);
        on(dec, 'click', function () {
          var v = parseInt(input.value || '0', 10);
          if (v > min) { input.value = v - 1; input.dispatchEvent(new Event('change', { bubbles: true })); }
        });
        on(inc, 'click', function () {
          var v = parseInt(input.value || '0', 10);
          input.value = v + 1; input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
    return { attach: attach };
  })();

  /* ---------- Accordion (native <details> — nothing to enhance beyond keyboard) ---------- */
  var Accordion = (function () {
    function attach() {
      // Native <details> handles most; nothing needed unless we want single-open mode.
    }
    return { attach: attach };
  })();

  /* ---------- Full-screen media viewer ---------- */
  var MediaViewer = (function () {
    var viewer, stage, releaseFocus, slides = [], index = 0;

    function collectSlides() {
      slides = qsa('[data-media-slide]').filter(function (s) {
        return !!qs('img', s);
      });
    }

    function open(startIndex) {
      viewer = qs('[data-media-viewer]');
      stage = qs('[data-media-viewer-stage]', viewer);
      if (!viewer) return;
      collectSlides();
      if (!slides.length) return;
      index = startIndex || 0;
      renderSlide();
      viewer.hidden = false;
      viewer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      releaseFocus = trapFocus(viewer, qs('[data-media-viewer-close]', viewer));
      document.addEventListener('keydown', keys);
    }

    function close() {
      if (!viewer) return;
      viewer.hidden = true;
      viewer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', keys);
      if (releaseFocus) releaseFocus();
    }

    function renderSlide() {
      if (!stage || !slides[index]) return;
      var img = qs('img', slides[index]);
      if (!img) return;
      var src = img.getAttribute('src').replace(/width=\d+/, 'width=2000');
      stage.innerHTML = '<img src="' + src + '" alt="' + (img.getAttribute('alt') || '') + '">';
    }

    function next() { if (!slides.length) return; index = (index + 1) % slides.length; renderSlide(); }
    function prev() { if (!slides.length) return; index = (index - 1 + slides.length) % slides.length; renderSlide(); }

    function keys(e) {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    }

    function attach() {
      document.addEventListener('click', function (e) {
        if (e.target.closest('[data-media-zoom]')) {
          e.preventDefault();
          var slide = e.target.closest('[data-media-slide]');
          collectSlides();
          var startIdx = slide ? slides.indexOf(slide) : 0;
          open(startIdx >= 0 ? startIdx : 0);
        }
      });
      on(qs('[data-media-viewer-close]'), 'click', close);
      on(qs('[data-media-viewer-prev]'), 'click', prev);
      on(qs('[data-media-viewer-next]'), 'click', next);
    }

    return { attach: attach };
  })();

  /* ---------- Product media gallery — Phase 2 (thumbnail switch + slide toggle) ---------- */
  var Gallery2 = (function () {
    function attach() {
      var media = qs('[data-product-media]');
      if (!media) return;
      var slides = qsa('[data-media-slide]', media);
      qsa('.main-product__thumb', media).forEach(function (thumb) {
        on(thumb, 'click', function () {
          var idx = parseInt(thumb.getAttribute('data-media-index') || '0', 10);
          slides.forEach(function (s, i) { s.classList.toggle('is-active', i === idx); });
          qsa('.main-product__thumb', media).forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
        });
      });
    }
    return { attach: attach };
  })();

  /* ---------- Filter drawer (mobile) ---------- */
  var Filters = (function () {
    var drawer;
    function open() {
      drawer = qs('[data-filter-drawer]');
      if (!drawer) return;
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
    }
    function attach() {
      qsa('[data-filter-toggle]').forEach(function (b) { on(b, 'click', open); });
      qsa('[data-filter-close]').forEach(function (b) { on(b, 'click', close); });
      // Auto-submit sort dropdown
      var sort = qs('[data-collection-sort]');
      if (sort) on(sort, 'change', function () { sort.form.submit(); });
    }
    return { attach: attach };
  })();

  /* ---------- Search tabs ---------- */
  var SearchTabs = (function () {
    function attach() {
      var tabsWrap = qs('[data-search-tabs]');
      if (!tabsWrap) return;
      var results = qs('[data-search-results]');
      qsa('[data-search-tab]', tabsWrap).forEach(function (tab) {
        on(tab, 'click', function () {
          var kind = tab.getAttribute('data-search-tab');
          qsa('[data-search-tab]', tabsWrap).forEach(function (t) { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
          tab.classList.add('is-active'); tab.setAttribute('aria-selected', 'true');
          qsa('[data-result-type]', results).forEach(function (r) {
            if (kind === 'all' || r.getAttribute('data-result-type') === kind) r.hidden = false;
            else r.hidden = true;
          });
        });
      });
    }
    return { attach: attach };
  })();

  /* ---------- Bag drawer render (client-side re-render after add/remove) ---------- */
  function renderBagDrawer(cart) {
    var body = qs('[data-bag-body]');
    if (!body) return;
    var count = qs('[data-bag-count]');
    if (count) count.textContent = cart.item_count;

    if (cart.item_count === 0) {
      body.innerHTML =
        '<div class="bag-drawer__empty">' +
        '<p class="bag-drawer__empty-heading">Your Bag is empty</p>' +
        '<p class="bag-drawer__empty-body">Continue exploring the House.</p>' +
        '<a href="/collections/all" class="button button--outline button--full">Continue exploring</a>' +
        '</div>';
      return;
    }

    var itemsHtml = cart.items.map(function (item) {
      var img = item.image ? '<img src="' + item.image.replace(/(_)?(\d+x\d*)?\.(jpg|jpeg|png|webp|gif)/, '_200x.$3') + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy">' : '';
      var variant = item.product_has_only_default_variant ? '' : '<p class="bag-drawer__item-variant">' + escapeHtml(item.variant_title || '') + '</p>';
      return '<li class="bag-drawer__item" data-key="' + item.key + '">' +
        '<a href="' + item.url + '" class="bag-drawer__item-media">' + img + '</a>' +
        '<div class="bag-drawer__item-body">' +
          '<a href="' + item.url + '" class="bag-drawer__item-title">' + escapeHtml(item.product_title) + '</a>' +
          variant +
          '<p class="bag-drawer__item-price">' + formatMoney(item.final_line_price) + '</p>' +
          '<div class="bag-drawer__item-controls">' +
            '<span class="bag-drawer__item-quantity">Quantity: ' + item.quantity + '</span>' +
            '<a href="' + item.url_to_remove + '" class="bag-drawer__item-remove" data-bag-remove data-key="' + item.key + '">Remove</a>' +
          '</div>' +
        '</div>' +
      '</li>';
    }).join('');

    body.innerHTML =
      '<ul class="bag-drawer__items" role="list" data-bag-items>' + itemsHtml + '</ul>' +
      '<div class="bag-drawer__footer">' +
        '<div class="bag-drawer__subtotal">' +
          '<span class="eyebrow-line">Subtotal</span>' +
          '<span data-bag-subtotal>' + formatMoney(cart.total_price) + '</span>' +
        '</div>' +
        '<p class="bag-drawer__tax-note">Taxes and shipping calculated at checkout.</p>' +
        '<a href="/cart/checkout" class="button button--solid button--full">Proceed to Checkout</a>' +
        '<a href="/cart" class="bag-drawer__view-bag">Continue to Bag &rarr;</a>' +
      '</div>';

    // Rebind remove links
    qsa('[data-bag-remove]', body).forEach(function (link) {
      on(link, 'click', function (e) {
        e.preventDefault();
        var key = link.getAttribute('data-key');
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        }).then(function (r) { return r.json(); }).then(renderBagDrawer);
      });
    });
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Extend original Bag.refresh so it also re-renders drawer content */
  var _refresh = Bag.refresh;
  Bag.refresh = function () {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = qs('[data-bag-count]');
        if (count) count.textContent = cart.item_count;
        renderBagDrawer(cart);
      })
      .catch(function () {});
  };

  /* ---------- Boot ---------- */
  function boot() {
    Bag.attach();
    Search.attach();
    Mobile.attach();
    Gallery.attach();
    Variants.attach();
    Quantity.attach();
    Accordion.attach();
    MediaViewer.attach();
    Gallery2.attach();
    Filters.attach();
    SearchTabs.attach();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
